import { format } from 'date-fns/esm';

import {
  isCollectionFilterDef,
  isDateFilterDef,
  isDateRangeFilterDef,
  isDateTimeRangeFilterDef,
  isSelectFilterOptionDef,
} from '../../components/DataTable/utils';

import type { FilterDefBase, FilterState } from '../../components/DataTable/types';

/**
 * filterDef에 정의된 key만 필터 상태로 변환한다.
 * @param query
 * @param defaultValue
 * @param filterDefMap
 */
export function convertQueryToFilterState(
  query: Record<string, unknown> | undefined,
  defaultValue: FilterState[],
  filterDefMap: Record<string, FilterDefBase>,
): FilterState[] {
  const filterState = Object.entries(query ?? {}).reduce((acc, [key, value]) => {
    const filterDef = filterDefMap[key];

    if (!filterDef) {
      return acc;
    }

    const queryValue = Array.isArray(value) ? value : [value];
    let states = queryValue.map(v => ({ key, value: v }));

    if (isSelectFilterOptionDef(filterDef) || isCollectionFilterDef(filterDef)) {
      if (Array.isArray(filterDef.options) && filterDef.options.length > 0) {
        const optionValues = filterDef.options.map(o => o.value);
        const optionValueType = typeof optionValues[0];
        const validValues = queryValue
          .map(v => {
            if (typeof v !== 'string') {
              return v;
            }

            switch (optionValueType) {
              case 'boolean':
                return v === 'true';
              case 'number': {
                const num = Number(v.trim());
                return isNaN(num) ? v : num;
              }
              default:
                return v;
            }
          })
          .filter(v => optionValues.includes(v));

        states = validValues.map(v => ({ key, value: v }));
      }
    } else if (isDateFilterDef(filterDef)) {
      const validValues = queryValue
        .map(v => {
          if (typeof v !== 'string') return null;

          const date = new Date(v);
          if (isNaN(date.getTime())) return null;

          return date;
        })
        .filter(v => v !== null);

      states = validValues.map(v => ({ key, value: v }));
    } else if (isDateRangeFilterDef(filterDef) || isDateTimeRangeFilterDef(filterDef)) {
      const validValues = queryValue.map(v => {
        if (typeof v !== 'string') return null;

        const parts = v.split(',');
        if (parts.length !== 2) return null;

        const start = new Date(parts[0]);
        const end = new Date(parts[1]);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

        if (start > end) return [end, start];
        return [start, end];
      });

      states = validValues.map(v => ({ key, value: v }));
    }

    return [...acc, ...states];
  }, [] as FilterState[]);

  return filterState.length > 0 ? filterState : defaultValue;
}

/**
 * filter 상태를 query 객체로 변환한다.
 * query 생성을 의도하므로, 데이터는 항상 string[]로 변환한다.
 * @param filters
 * @param filterDefMap
 */
export function convertFilterToQueryState(
  filters: FilterState[],
  filterDefMap: Record<string, FilterDefBase>,
): Record<string, unknown[]> {
  /**
   * 일단 모든 필터를 null로 초기화한다.
   */
  const initialResult: Record<string, unknown[]> = Object.keys(filterDefMap).reduce(
    (acc, key) => ({ ...acc, [key]: null }),
    {},
  );
  return filters.reduce<Record<string, unknown[]>>((acc, item) => {
    const filterDef = filterDefMap[item.key];

    if (!filterDef) {
      return acc;
    }

    if (!acc[item.key]) {
      acc[item.key] = [];
    }

    let queryValue = item.value;

    if (isDateFilterDef(filterDef)) {
      if (!(queryValue instanceof Date)) {
        queryValue = null;
      } else {
        queryValue = format(queryValue, 'yyyy-MM-dd');
      }
    } else if (isDateRangeFilterDef(filterDef)) {
      if (!Array.isArray(queryValue) || queryValue.length !== 2) {
        queryValue = null;
      } else {
        const [start, end] = queryValue;
        queryValue = `${format(start, 'yyyy-MM-dd')},${format(end, 'yyyy-MM-dd')}`;
      }
    } else if (isDateTimeRangeFilterDef(filterDef)) {
      if (!Array.isArray(queryValue) || queryValue.length !== 2) {
        queryValue = null;
      } else {
        const [start, end] = queryValue;
        queryValue = `${new Date(start).toISOString()},${new Date(end).toISOString()}`;
      }
    }

    acc[item.key].push(queryValue);

    return acc;
  }, initialResult);
}

/**
 * filterDefMap을 기준으로 filterState에서 무효 값을 제거한다.
 * @description
 * - select/collection 타입은 options가 채워졌을 때만 검증하고, options에 없는 value는 제거한다.
 * - options가 비어있으면(비동기 로딩 중) 보존한다.
 * - filterDef 자체가 사라진 key도 제거한다.
 * - 변경이 없으면 동일 참조를 반환해 불필요한 리렌더/effect 루프를 방지한다.
 */
export function pruneInvalidFilterState(
  filterState: FilterState[],
  filterDefMap: Record<string, FilterDefBase>,
): FilterState[] {
  let changed = false;
  const next = filterState.filter(item => {
    const filterDef = filterDefMap[item.key];

    if (!filterDef) {
      changed = true;
      return false;
    }

    if (!isSelectFilterOptionDef(filterDef) && !isCollectionFilterDef(filterDef)) {
      return true;
    }

    if (!Array.isArray(filterDef.options) || filterDef.options.length === 0) {
      return true;
    }

    const optionValues = filterDef.options.map(o => o.value);
    if (!optionValues.includes(item.value)) {
      changed = true;
      return false;
    }

    return true;
  });

  return changed ? next : filterState;
}
